import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { EmployeeId } from '@modernloop/shared/ids';
import { Avatar, Stack, Typography } from '@mui/material';

import { useVideoMeetingHostEmployeeValueQuery } from 'src/generated/mloop-graphql';

type Props = {
  /**
   * The employee ID to fetch and display
   */
  employeeId: EmployeeId;
};

/**
 * VideoMeetingHostEmployeeValue component
 *
 * Fetches and displays employee information for a video meeting host.
 * Shows the employee's avatar, full name, or email.
 *
 * @param employeeId - The ID of the employee to fetch
 * @returns A Stack component with the employee's avatar and name
 */
const VideoMeetingHostEmployeeValue: React.FC<Props> = ({ employeeId }) => {
  const { t } = useTranslation();
  const { data, loading } = useVideoMeetingHostEmployeeValueQuery({
    variables: { employeeId },
    skip: !employeeId,
  });

  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Avatar sx={{ width: 24, height: 24 }} />
        <Typography noWrap>{t('hiring-dashboard-common:common.loading')}</Typography>
      </Stack>
    );
  }

  const employee = data?.employeeByIds?.[0];
  if (!employee) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Avatar sx={{ width: 24, height: 24 }} />
        <Typography noWrap>{t('hiring-dashboard-common:common.unknown')}</Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Avatar
        src={employee.internalProfilePictureUrl || undefined}
        alt={employee.fullName || ''}
        sx={{ width: 24, height: 24 }}
      />
      <Typography noWrap>{employee.fullName || employee.email}</Typography>
    </Stack>
  );
};

export const VIDEO_MEETING_HOST_EMPLOYEE_VALUE_QUERY = gql`
  query VideoMeetingHostEmployeeValue($employeeId: uuid!) {
    employeeByIds(ids: [$employeeId]) {
      id
      fullName
      email
      internalProfilePictureUrl
    }
  }
`;

export default VideoMeetingHostEmployeeValue;
